import { ExecutionEnvironment, PipelineEnvironment, StepAction, StepTask } from '@telespot/domain';

import { GenerateFindingValue, PipelineStepCategory } from '../../types';

export type CategoryUpsertConflict = 'duplicate' | 'single_category_only';

/**
 * Behavior flags that may be set on a pipeline step draft. Only the flags
 * relevant to the (environment, task) combination are preserved; the others
 * are forced to `false`.
 */
export interface PipelineStepBehaviorFlags {
  multiple: boolean;
  allowNewOptions: boolean;
  enableMosaicView: boolean;
  counter: boolean;
}

const SINGLE_CATEGORY_TASKS: ReadonlySet<StepTask> = new Set([StepTask.TEXT, StepTask.CLASSIFICATION]);

const DETECTION_FAMILY_TASKS: ReadonlySet<StepTask> = new Set([
  StepTask.ROIDETECTION,
  StepTask.POSITION,
  StepTask.SEGMENTATION,
  StepTask.ROITRACKING,
]);

const UNIQUE_PER_PIPELINE_TASKS: ReadonlySet<StepTask> = new Set([
  StepTask.POSITION,
  StepTask.ROIDETECTION,
  StepTask.ROITRACKING,
]);

/**
 * The decision returned by {@link PipelineIntegrityService.evaluateTaskAvailability}.
 * `allowed` means the task can be selected; every other kind names the rule
 * that blocks it, so the UI layer can map the kind onto a localized reason.
 */
export type TaskAvailability =
  | { kind: 'allowed' }
  | { kind: 'environment' }
  | { kind: 'order_zero' }
  | { kind: 'already_used' }
  | { kind: 'incompatible_with_categories' };

/**
 * Inputs to {@link PipelineIntegrityService.evaluateTaskAvailability}.
 */
export interface TaskAvailabilityContext {
  env: PipelineEnvironment | null;
  order: number | null;
  otherStepsTasks: StepTask[];
  currentTask: StepTask | null;
  hasCategories: boolean;
}

/**
 * Domain service that encodes the validity rules of a pipeline step: which
 * (environment, task) combinations require an algorithm or categories,
 * which behavior flags they accept, and how to resolve the trigger and
 * finding actions from the editor's "cloud subject".
 */
export class PipelineIntegrityService {
  /**
   * Whether the given task requires an algorithm executor to run.
   *
   * @param task - The pipeline step task
   * @returns `true` when the task can only run on an algorithm
   */
  public static algorithmRequired(task: StepTask): boolean {
    return task === StepTask.MAPPING || task === StepTask.OCR;
  }

  /**
   * A pipeline's display name is valid when the English translation is
   * a non-empty trimmed string. This is the contract `SavePipelineCommand`
   * enforces on its way to persistence; the UI mirrors it for live validity
   * feedback.
   */
  public static isDisplayNameValid(name: { [lang: string]: string } | undefined | null): boolean {
    const en = name?.['en'];
    return typeof en === 'string' && en.trim().length > 0;
  }

  /**
   * Whether an algorithm executor is allowed for the (environment, task)
   * combination.
   *
   * @param env - The pipeline environment
   * @param task - The pipeline step task
   * @returns `true` when the combination supports running on an algorithm
   */
  public static algorithmCompatible(env: PipelineEnvironment, task: StepTask): boolean {
    if (env === PipelineEnvironment.CLOUD) {
      return task === StepTask.ROIDETECTION || task === StepTask.POSITION || task === StepTask.SEGMENTATION;
    }
    return (
      task === StepTask.CLASSIFICATION ||
      task === StepTask.ROIDETECTION ||
      task === StepTask.SEGMENTATION ||
      task === StepTask.POSITION ||
      task === StepTask.MAPPING ||
      task === StepTask.OCR
    );
  }

  /**
   * Whether the (environment, task) combination requires the user to
   * configure at least one category.
   *
   * @param env - The pipeline environment
   * @param task - The pipeline step task
   * @returns `true` when categories must be provided
   */
  public static categoryRequired(env: PipelineEnvironment, task: StepTask): boolean {
    if (task === StepTask.MAPPING) return false;
    if (env === PipelineEnvironment.EDGE && (task === StepTask.TEXT || task === StepTask.OCR)) {
      return false;
    }
    return true;
  }

  /**
   * Whether the task accepts at most a single category.
   *
   * @param task - The pipeline step task
   * @returns `true` for tasks that are restricted to one category
   */
  public static isSingleCategoryTask(task: StepTask): boolean {
    return SINGLE_CATEGORY_TASKS.has(task);
  }

  /**
   * Whether the (environment, task) combination is supported at all. EDGE
   * pipelines do not run TEXT or ROITRACKING steps; CLOUD pipelines do not
   * run OCR or MAPPING steps.
   *
   * @param env - The pipeline environment
   * @param task - The pipeline step task
   * @returns `true` when the task can be selected for that environment
   */
  public static isTaskCompatibleWithEnvironment(env: PipelineEnvironment, task: StepTask): boolean {
    if (env === PipelineEnvironment.EDGE) {
      return task !== StepTask.TEXT && task !== StepTask.ROITRACKING;
    }
    return task !== StepTask.OCR && task !== StepTask.MAPPING;
  }

  /**
   * Whether the task may appear at most once across all steps of a pipeline.
   * Detection-style tasks that produce ROIs (POSITION, ROIDETECTION,
   * ROITRACKING) are unique; all other tasks may be repeated.
   *
   * @param task - The pipeline step task
   * @returns `true` when only a single step of this task is allowed per pipeline
   */
  public static isUniquePerPipeline(task: StepTask): boolean {
    return UNIQUE_PER_PIPELINE_TASKS.has(task);
  }

  /**
   * Whether switching the editor from `from` to `to` preserves the categories
   * already configured on the step. The two compatible families are
   * TEXT↔OCR (single-category, no options) and the detection family
   * (ROIDETECTION, POSITION, SEGMENTATION, ROITRACKING — multi-category with
   * options). Any other transition would discard the categories.
   *
   * @param from - The currently selected task
   * @param to - The candidate task
   * @returns `true` when the transition keeps the existing categories valid
   */
  public static taskTransitionPreservesCategories(from: StepTask, to: StepTask): boolean {
    if ((from === StepTask.TEXT || from === StepTask.OCR) && (to === StepTask.TEXT || to === StepTask.OCR)) {
      return true;
    }
    if (DETECTION_FAMILY_TASKS.has(from) && DETECTION_FAMILY_TASKS.has(to)) {
      return true;
    }
    return false;
  }

  /**
   * Resolves whether a task may be selected in the current editor context.
   *
   * Precedence (first match wins):
   *   1. No environment chosen yet → `environment`.
   *   2. Task not supported by the environment → `environment`.
   *   3. MAPPING at order 0 → `order_zero` (MAPPING needs a producing step before it).
   *   4. One-of-a-kind task already used by another step → `already_used`.
   *   5. Switching tasks while categories exist, across families → `incompatible_with_categories`.
   *   6. Otherwise → `allowed`.
   *
   * @param task - The candidate task being evaluated
   * @param ctx - The editor context (env, order, sibling tasks, current task, categories)
   * @returns The availability decision
   */
  public static evaluateTaskAvailability(task: StepTask, ctx: TaskAvailabilityContext): TaskAvailability {
    if (ctx.env === null || !this.isTaskCompatibleWithEnvironment(ctx.env, task)) {
      return { kind: 'environment' };
    } else if (task === StepTask.MAPPING && ctx.order === 0) {
      return { kind: 'order_zero' };
    } else if (this.isUniquePerPipeline(task) && ctx.otherStepsTasks.includes(task)) {
      return { kind: 'already_used' };
    } else if (
      ctx.currentTask !== null &&
      ctx.currentTask !== task &&
      ctx.hasCategories &&
      !this.taskTransitionPreservesCategories(ctx.currentTask, task)
    ) {
      return { kind: 'incompatible_with_categories' };
    } else {
      return { kind: 'allowed' };
    }
  }

  /**
   * Default value the editor pre-selects for the cloud-subject field when a
   * task is first chosen on CLOUD. Kept here so the literal lives next to the
   * other cloud-subject rules ({@link resolveTrigger}, {@link cloudSubjectFromTrigger}).
   */
  public static defaultCloudSubject(): StepAction {
    return StepAction.SAMPLE_CREATED;
  }

  /**
   * Default value the editor pre-selects for the generate-finding field when
   * a task is first chosen on EDGE. Kept here so the literal lives next to
   * the other generate-finding rules ({@link resolveGenerateFinding},
   * {@link generateFindingForEdit}).
   */
  public static defaultGenerateFinding(): StepAction {
    return StepAction.ASSET_CREATED;
  }

  /**
   * Whether the editor must surface the cloud-subject selector for the given
   * environment. Callers should additionally gate on the presence of a task.
   */
  public static showsCloudSubject(env: PipelineEnvironment): boolean {
    return env === PipelineEnvironment.CLOUD;
  }

  /**
   * Whether the editor must surface the generate-finding selector for the
   * given environment. Callers should additionally gate on the presence of a
   * task.
   */
  public static showsGenerateFinding(env: PipelineEnvironment): boolean {
    return env === PipelineEnvironment.EDGE;
  }

  /**
   * Whether the `multiple` behavior flag applies to the (environment, task)
   * combination.
   */
  public static supportsMultiple(env: PipelineEnvironment, task: StepTask): boolean {
    return env === PipelineEnvironment.CLOUD && task === StepTask.CLASSIFICATION;
  }

  /**
   * Whether the `allowNewOptions` behavior flag applies to the (environment,
   * task) combination.
   */
  public static supportsAllowNewOptions(env: PipelineEnvironment, task: StepTask): boolean {
    return (
      env === PipelineEnvironment.CLOUD &&
      (task === StepTask.ROIDETECTION || task === StepTask.POSITION || task === StepTask.SEGMENTATION)
    );
  }

  /**
   * Whether the `enableMosaicView` behavior flag applies to the
   * (environment, task) combination.
   */
  public static supportsMosaicView(env: PipelineEnvironment, task: StepTask): boolean {
    return env === PipelineEnvironment.CLOUD && (task === StepTask.ROIDETECTION || task === StepTask.POSITION);
  }

  /**
   * Whether the `counter` behavior flag applies to the (environment, task)
   * combination.
   */
  public static supportsCounter(env: PipelineEnvironment, task: StepTask): boolean {
    return (
      env === PipelineEnvironment.EDGE &&
      (task === StepTask.ROIDETECTION || task === StepTask.SEGMENTATION || task === StepTask.POSITION)
    );
  }

  /**
   * Whether label colours are relevant for the (environment, task)
   * combination. Cloud classification results never render label colour
   * anywhere, so offering to configure one there would be misleading; Edge
   * classification and the detection-family tasks all render colours (Edge
   * devices, ROI overlays).
   */
  public static supportsLabelColors(env: PipelineEnvironment, task: StepTask): boolean {
    return !(env === PipelineEnvironment.CLOUD && task === StepTask.CLASSIFICATION);
  }

  /**
   * Whether the (environment, task) combination represents an EDGE mapping
   * step (the editor surfaces a mapping placeholder for this case).
   */
  public static isMappingStep(env: PipelineEnvironment, task: StepTask): boolean {
    return env === PipelineEnvironment.EDGE && task === StepTask.MAPPING;
  }

  /**
   * Whether the task uses the basic (single-value) category editor variant
   * instead of the full options-list variant.
   */
  public static isBasicCategoryTask(task: StepTask): boolean {
    return task === StepTask.TEXT;
  }

  /**
   * Filters the requested behavior flags down to those that apply to the
   * (environment, task) combination. Flags that do not apply are forced to
   * `false`.
   *
   * @param env - The pipeline environment
   * @param task - The pipeline step task
   * @param flags - The flag values requested by the editor
   * @returns The flags that are valid for the combination
   */
  public static flagsForTask(
    env: PipelineEnvironment,
    task: StepTask,
    flags: PipelineStepBehaviorFlags
  ): PipelineStepBehaviorFlags {
    return {
      multiple: this.supportsMultiple(env, task) ? flags.multiple : false,
      allowNewOptions: this.supportsAllowNewOptions(env, task) ? flags.allowNewOptions : false,
      enableMosaicView: this.supportsMosaicView(env, task) ? flags.enableMosaicView : false,
      counter: this.supportsCounter(env, task) ? flags.counter : false,
    };
  }

  /**
   * Resolves the step trigger from the environment. On EDGE the trigger is
   * always `ASSET_CREATED`; on CLOUD it follows the editor's "cloud subject".
   *
   * @param env - The pipeline environment
   * @param cloudSubject - The user-selected cloud subject
   * @returns The trigger action, or `null` if it cannot be resolved
   */
  public static resolveTrigger(env: PipelineEnvironment, cloudSubject: StepAction | null): StepAction | null {
    if (env === PipelineEnvironment.CLOUD) return cloudSubject ?? null;
    return StepAction.ASSET_CREATED;
  }

  /**
   * Resolves the `generateFinding` value from the environment. On CLOUD the
   * "cloud subject" doubles as the generateFinding; on EDGE it is the user's
   * explicit choice.
   *
   * @param env - The pipeline environment
   * @param cloudSubject - The user-selected cloud subject
   * @param generateFinding - The explicit generateFinding choice
   * @returns The generateFinding value, or `null` if it cannot be resolved
   */
  public static resolveGenerateFinding(
    env: PipelineEnvironment,
    cloudSubject: StepAction | null,
    generateFinding: GenerateFindingValue | null
  ): GenerateFindingValue | null {
    if (env === PipelineEnvironment.CLOUD) return cloudSubject ?? null;
    return generateFinding ?? null;
  }

  /**
   * Maps the pipeline environment onto the corresponding algorithm
   * execution environment.
   *
   * @param env - The pipeline environment
   * @returns The execution environment to run algorithms in
   */
  public static toExecutionEnvironment(env: PipelineEnvironment): ExecutionEnvironment {
    return env === PipelineEnvironment.EDGE ? ExecutionEnvironment.MOBILE : ExecutionEnvironment.CLOUD;
  }

  /**
   * Derives the editor's "cloud subject" from a stored trigger. On CLOUD the
   * cloud subject mirrors the trigger when it is one of the cloud-eligible
   * actions; on EDGE the editor does not expose a cloud subject.
   *
   * @param env - The pipeline environment
   * @param trigger - The stored trigger action
   * @returns The cloud subject for the editor, or `null` when not applicable
   */
  public static cloudSubjectFromTrigger(env: PipelineEnvironment, trigger: StepAction): StepAction | null {
    if (env !== PipelineEnvironment.CLOUD) return null;
    return trigger === StepAction.SAMPLE_CREATED || trigger === StepAction.ASSET_CREATED ? trigger : null;
  }

  /**
   * Derives the editor's `generateFinding` selection from a stored value. On
   * CLOUD the editor uses the cloud subject instead of an explicit
   * generateFinding; on EDGE the stored value is surfaced as-is.
   *
   * @param env - The pipeline environment
   * @param generateFinding - The stored generateFinding value
   * @returns The generateFinding for the editor, or `null` when not applicable
   */
  public static generateFindingForEdit(
    env: PipelineEnvironment,
    generateFinding: GenerateFindingValue
  ): GenerateFindingValue | null {
    if (env !== PipelineEnvironment.EDGE) return null;
    return generateFinding ?? null;
  }

  /**
   * Resolves which constraint, if any, prevents the candidate category from
   * being upserted into the current category list. Encapsulates the rules of
   * uniqueness and single-category-only tasks so the UI layer can map the
   * result onto error keys without knowing the rules.
   *
   * @param categories - The current category list
   * @param candidate - The category being added or edited
   * @param options - `editingUuid` is the slot being edited (`null` when
   * adding a new category); `task` is the current task selection
   * @returns The conflict kind, or `null` when no conflict applies
   */
  public static findCategoryUpsertConflict(
    categories: PipelineStepCategory[],
    candidate: PipelineStepCategory,
    options: { editingUuid: string | null; task: StepTask | null }
  ): CategoryUpsertConflict | null {
    const { editingUuid, task } = options;
    if (editingUuid !== null) {
      const editingIndex = categories.findIndex((c) => c.uuid === editingUuid);
      if (editingIndex === -1) return null;
      const collides =
        candidate.uuid !== editingUuid && categories.some((c, i) => i !== editingIndex && c.uuid === candidate.uuid);
      return collides ? 'duplicate' : null;
    }

    if (categories.some((c) => c.uuid === candidate.uuid)) return 'duplicate';
    if (task !== null && this.isSingleCategoryTask(task) && categories.length >= 1) {
      return 'single_category_only';
    }
    return null;
  }
}
